import { useContext, useEffect, useRef, useState } from "react";
import BreadCrumb from "../../components/common/BreadCrumb";
import PaginationLayout from "../../components/common/PaginationLayout";
import { GlobalContext, handleError } from "../../context/Provider";
import Spinner from "../../components/common/spinner";
import henceforthApi from "../../utils/henceforthApi";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";

const LIMIT = 10;

const Store = () => {
    const breadCrumbPath = [
        { name: `Home`, url: `/`, active: "" },
        { name: `Store Listing`, url: ``, active: "not-allowed" }
    ];

    const router = useNavigate();
    const location = useLocation();

    const newParam = new URLSearchParams(location.search);

    const { language_data, loading, setLoading, authState, authDispatch } = useContext(GlobalContext);

    const [page, setPage] = useState<number>(Number(newParam.get("page")) || 1);

    // CHANGED: default sortOrder is now "" (no sort) instead of "asc" — previously
    // defaulting to "asc" meant the very first click on ANY icon (up or down) always
    // computed newOrder as "asc" too, because the old toggle only checked
    // "is it currently ascending on this field", never which icon was actually clicked.
    const [sortField, setSortField] = useState(newParam.get("sort_by") || "");
    const [sortOrder, setSortOrder] = useState(newParam.get("sort_order") || "");

    // ADDED: single place that pushes sort state + URL params.
    // When order is "" (no sort), sort_by/sort_order are removed from the URL
    // entirely instead of being set to empty strings, so the listing goes back
    // to its real/default order.
    const applySort = (field: string, order: string) => {
        setSortField(field);
        setSortOrder(order);

        const params = new URLSearchParams(location.search);
        if (order) {
            params.set("sort_by", field);
            params.set("sort_order", order);
        } else {
            params.delete("sort_by");
            params.delete("sort_order");
        }

        const currentPage = page || "1";
        router(`/store?${params.toString()}`);
    };

    // REPLACED: old single onSort(field) that both icons shared.
    // onSort(field) // REMOVED

    // ADDED: independent handler for the up (ascending) arrow.
    // Clicking it when it's already active clears the sort (back to default).
    // Clicking it otherwise always sets this field to ascending, regardless of
    // whatever the current sort state was.
    const onSortAsc = (field: string) => {
        if (sortField === field && sortOrder === "asc") {
            applySort("", "");
        } else {
            applySort(field, "asc");
        }
    };

    // ADDED: independent handler for the down (descending) arrow. Same idea, mirrored.
    const onSortDesc = (field: string) => {
        if (sortField === field && sortOrder === "desc") {
            applySort("", "");
        } else {
            applySort(field, "desc");
        }
    };

    const SortIcon = ({ field }: { field: string }) => (
        <span
            className="ms-1"
            style={{ display: "inline-flex", gap: "4px", verticalAlign: "middle" }}
        >
            <i
                className="fa fa-arrow-up"
                style={{
                    fontSize: "13px",
                    cursor: "pointer",
                    color: sortField === field && sortOrder === "asc" ? "#29ABE2" : "#b0b8c1"
                }}
                onClick={() => onSortAsc(field)}
            />
            <i
                className="fa fa-arrow-down"
                style={{
                    fontSize: "13px",
                    cursor: "pointer",
                    color: sortField === field && sortOrder === "desc" ? "#e03131" : "#b0b8c1"
                }}
                onClick={() => onSortDesc(field)}
            />
        </span>
    );

    const [storeL, setStoreL] = useState<{ total_count: number; data: any[] }>({
        total_count: 0,
        data: []
    });

    // Bulk upload state
    const [bulkModal, setBulkModal] = useState(false);
    const [bulkFile, setBulkFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ADDED: holds the summary + skipped_details returned from the bulk upload API
    // so we can show the user exactly which rows failed and why.
    const [uploadResult, setUploadResult] = useState<{
        total_uploaded: number;
        total_created: number;
        total_skipped: number;
        skipped_details: { row: number; email?: string; phone?: string; name?: string; reason: string }[];
    } | null>(null);

    // ADDED: controls the separate "results" modal shown after an upload attempt.
    const [showResultModal, setShowResultModal] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────
    const getStoreList = async (currentPage: number) => {
        setLoading(true);
        try {
            const params = new URLSearchParams(location.search);
            params.delete("page");
            params.set("pagination", String(currentPage - 1));
            params.set("limit", String(LIMIT));

            const res = (
                await henceforthApi.Store.getStoreList(
                    params.toString()
                )
            ).data;

            setStoreL({
                total_count: res.total_count,
                data: res.data
            });
        } catch (err: any) {
            setStoreL({ total_count: 0, data: [] });
            handleError(err, "active", authDispatch);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const currentPage = Number(newParam.get("page")) || 1;
        setPage(currentPage);
        getStoreList(currentPage);
    }, [
        newParam.get("page"),
        newParam.get("search"),
        newParam.get("sort_by"),
        newParam.get("sort_order"),
    ]);

    // ─── Search ───────────────────────────────────────────────────────────────
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        const params = new URLSearchParams(location.search);
        if (value) {
            params.set("search", value);
        } else {
            params.delete("search");
        }
        params.set("page", "1");
        setPage(1);
        router(`/store?${params.toString()}`);
    };

    // const SortIcon = ({ field }: { field: string }) => (
    //     <span
    //         className="ms-1"
    //         style={{
    //             cursor: "pointer",
    //             display: "inline-flex",
    //             gap: "4px",
    //             verticalAlign: "middle",
    //         }}
    //         onClick={() => onSort(field)}
    //     >
    //         <i
    //             className="fa fa-arrow-up"
    //             style={{
    //                 fontSize: "13px",
    //                 color:
    //                     sortField === field && sortOrder === "asc"
    //                         ? "#29ABE2"
    //                         : "#b0b8c1",
    //             }}
    //         />
    //         <i
    //             className="fa fa-arrow-down"
    //             style={{
    //                 fontSize: "13px",
    //                 color:
    //                     sortField === field && sortOrder === "desc"
    //                         ? "#e03131"
    //                         : "#b0b8c1",
    //             }}
    //         />
    //     </span>
    // );

    // ─── Page change ──────────────────────────────────────────────────────────
    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(location.search);
        params.set("page", String(newPage));
        setPage(newPage);
        router(`/store?${params.toString()}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ─── Bulk upload ──────────────────────────────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) setBulkFile(e.target.files[0]);
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) return toast.error("Please select a CSV file");
        if (!bulkFile.name.endsWith(".csv") && !bulkFile.type.includes("csv"))
            return toast.error("Only CSV files are allowed");

        const fd = new FormData();
        fd.append("file", bulkFile);
        setUploading(true);
        try {
            const res = await henceforthApi.Store.addBulkStore(authState?._id, fd);
            const body = res?.data;

            toast.success(body?.message || "Bulk upload successful");

            // ADDED: capture the summary + skip reasons from a successful (2xx) response
            setUploadResult({
                total_uploaded: body?.total_uploaded ?? 0,
                total_created: body?.total_created ?? 0,
                total_skipped: body?.total_skipped ?? 0,
                skipped_details: body?.skipped_details ?? []
            });

            getStoreList(1);
            closeBulkModal();

            // ADDED: only pop the results modal if something was actually skipped —
            // no point showing an empty "skip report" for a clean upload
            if ((body?.skipped_details?.length ?? 0) > 0) {
                setShowResultModal(true);
            }
        } catch (err: any) {
            let data: any = {};

            try {
                data =
                    err?.response?.data ||               // CHANGED: axios puts the parsed JSON body here
                    err?.response?.body ||               // sometimes parsed
                    (err?.response?.text ? JSON.parse(err.response.text) : {}) ||
                    err?.body ||
                    {};
            } catch (e) {
                data = {};
            }

            const conflict = data?.conflict;

            const email = conflict?.emails?.[0];
            const phone = conflict?.phones?.[0];

            if (email) {
                toast.error(`Email already exists: ${email}`);
            } else if (phone) {
                toast.error(`Phone already exists: ${phone}`);
            } else {
                toast.error(data?.message || "Upload failed");
            }

            // ADDED: even on a failed (409/400) response, the backend still returns
            // total_uploaded/total_created/total_skipped/skipped_details — show them too
            if (data?.skipped_details?.length || data?.total_uploaded) {
                setUploadResult({
                    total_uploaded: data?.total_uploaded ?? 0,
                    total_created: data?.total_created ?? 0,
                    total_skipped: data?.total_skipped ?? 0,
                    skipped_details: data?.skipped_details ?? []
                });
                setShowResultModal(true);
            }
        } finally {
            setUploading(false);
        }
    };

    const closeBulkModal = () => {
        setBulkModal(false);
        setBulkFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ADDED: close handler for the results modal
    const closeResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className="page-spacing">
                <section className="products">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="common-card">

                                    {/* ── Header ── */}
                                    <div className="common-card-title d-flex justify-content-between align-items-center flex-column flex-sm-row gap-2">
                                        <h5>
                                            {language_data?.[authState.lang]?.common_store || "Store"}
                                        </h5>
                                        <div className="d-flex gap-2 flex-wrap align-items-center">

                                            {/* Search */}
                                            <div className="input-group input-group-sm" style={{ width: 240 }}>
                                                <span className="input-group-text">
                                                    <i className="fa fa-search" />
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search name, email, company..."
                                                    defaultValue={newParam.get("search") ?? ""}
                                                    onChange={handleSearch}
                                                />
                                            </div>

                                            {/* Download Sample */}
                                            <button
                                                className="btn btn-white btn-sm"
                                                onClick={() => {
                                                    const link = document.createElement("a");
                                                    link.href = `${process.env.PUBLIC_URL}/assets/csv/Store.csv`;
                                                    link.download = "Store.csv";
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }}
                                            >
                                                <i className="fa fa-download me-1" />
                                                {language_data?.[authState.lang]?.common_download_sample || "Download Sample"}
                                            </button>

                                            {/* Add Store */}
                                            <button
                                                className="btn btn-theme btn-sm"
                                                onClick={() => router("/store/add")}
                                            >
                                                {language_data?.[authState.lang]?.store_add_store || "Add Store"}
                                            </button>

                                            {/* Bulk Upload */}
                                            <button
                                                className="btn btn-white btn-sm"
                                                onClick={() => setBulkModal(true)}
                                            >
                                                <i className="fa fa-upload me-1" />
                                                {language_data?.[authState.lang]?.common_bulk_upload || "Bulk Upload"}
                                            </button>

                                            {/* ADDED: quick re-open of the last upload's skip report, if any exists */}
                                            {uploadResult && uploadResult.skipped_details.length > 0 && (
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => setShowResultModal(true)}
                                                >
                                                    <i className="fa fa-exclamation-triangle me-1" />
                                                    View Skip Report ({uploadResult.skipped_details.length})
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── Table ── */}
                                    <div className="common-card-content">
                                        <div className="data-list-table table-responsive mb-4 text-center">
                                            {loading ? (
                                                <Spinner color="text-success" />
                                            ) : (
                                                <table className="table table-striped align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>{language_data?.[authState.lang]?.common_sr_no}.</th>
                                                            <th>{language_data?.[authState.lang]?.common_name} <SortIcon field="name" /></th>
                                                            <th>{language_data?.[authState.lang]?.common_email} <SortIcon field="email" /></th>
                                                            <th>{language_data?.[authState.lang]?.common_company_name} <SortIcon field="company" /></th>
                                                            <th>{language_data?.[authState.lang]?.common_phone_number}</th>
                                                            <th>{language_data?.[authState.lang]?.common_address}</th>
                                                            <th>{language_data?.[authState.lang]?.common_actions || "Actions"}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Array.isArray(storeL.data) && storeL.data.length ? (
                                                            storeL.data.map((item: any, index: number) => (
                                                                <tr key={item._id}>
                                                                    <td>{(page - 1) * LIMIT + (index + 1)}</td>
                                                                    <td>{item.name || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>{item.email || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>{item.company || "—"}</td>
                                                                    <td>{`+${item.country_code} ${item.phone_number}`}</td>
                                                                    <td>{item.full_address || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-theme"
                                                                            onClick={() => router(`/store/${item._id}`)}
                                                                        >
                                                                            <i className="fa fa-eye me-1" />
                                                                            {language_data?.[authState.lang]?.common_view || "View"}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={7} style={{ textAlign: "center" }}>
                                                                    {language_data?.[authState.lang]?.common_no_data_found || "No data found"}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                        <PaginationLayout
                                            data={storeL.data}
                                            count={storeL.total_count}
                                            limit={LIMIT}
                                            page={page}
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* ── Bulk Upload Modal ── */}
            {bulkModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {language_data?.[authState.lang]?.common_bulk_upload || "Bulk Upload Stores"}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeBulkModal} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">
                                        {language_data?.[authState.lang]?.common_select_csv || "Select CSV File"}
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />
                                    <small className="text-muted">
                                        Only CSV files are allowed. The file must contain required columns.
                                    </small>
                                </div>
                                {bulkFile && (
                                    <div className="alert alert-info">
                                        <i className="fa fa-file-text-o me-2" />
                                        {bulkFile.name} ({(bulkFile.size / 1024).toFixed(2)} KB)
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeBulkModal}>
                                    {language_data?.[authState.lang]?.common_cancel || "Cancel"}
                                </button>
                                <button
                                    className="btn btn-theme"
                                    onClick={handleBulkUpload}
                                    disabled={uploading || !bulkFile}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            {language_data?.[authState.lang]?.common_uploading || "Uploading..."}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-upload me-1" />
                                            {language_data?.[authState.lang]?.common_upload || "Upload"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ADDED: Skip Report Modal ── */}
            {showResultModal && uploadResult && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fa fa-exclamation-triangle text-warning me-2" />
                                    Bulk Upload Report
                                </h5>
                                <button type="button" className="btn-close" onClick={closeResultModal} />
                            </div>
                            <div className="modal-body">
                                {/* Summary counters */}
                                <div className="d-flex gap-3 mb-3 flex-wrap">
                                    <div className="border rounded p-2 px-3 text-center flex-fill">
                                        <div className="fw-bold fs-5">{uploadResult.total_uploaded}</div>
                                        <small className="text-muted">Uploaded</small>
                                    </div>
                                    <div className="border rounded p-2 px-3 text-center flex-fill">
                                        <div className="fw-bold fs-5 text-success">{uploadResult.total_created}</div>
                                        <small className="text-muted">Created</small>
                                    </div>
                                    <div className="border rounded p-2 px-3 text-center flex-fill">
                                        <div className="fw-bold fs-5 text-danger">{uploadResult.total_skipped}</div>
                                        <small className="text-muted">Skipped</small>
                                    </div>
                                </div>

                                {/* Row-by-row skip reasons */}
                                {uploadResult.skipped_details.length > 0 ? (
                                    <div className="table-responsive" style={{ maxHeight: "350px", overflowY: "auto" }}>
                                        <table className="table table-sm table-bordered align-middle mb-0">
                                            <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                                                <tr>
                                                    <th>Row</th>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Phone</th>
                                                    <th>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {uploadResult.skipped_details.map((detail, idx) => (
                                                    <tr key={`${detail.row}-${idx}`}>
                                                        <td>{detail.row}</td>
                                                        <td>{detail.name || "—"}</td>
                                                        <td>{detail.email || "—"}</td>
                                                        <td>{detail.phone || "—"}</td>
                                                        <td className="text-danger">{detail.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-muted mb-0">No rows were skipped.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeResultModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Store;